import html2canvas from 'html2canvas';
import { PdfReportError, logPdfWarning } from './pdf-report-errors';

const IMAGE_FETCH_TIMEOUT_MS = 8000;
const IMAGE_INLINE_TIMEOUT_MS = 12000;

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

export function getRenderScale(): number {
  return isIOS() ? 1.4 : 2;
}

export async function inlineImagesAsDataUrls(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(img => withTimeout(inlineSingleImage(img), IMAGE_INLINE_TIMEOUT_MS))
  );
}

async function inlineSingleImage(img: HTMLImageElement): Promise<void> {
  const src = img.getAttribute('src');
  if (!src || src.startsWith('data:')) return;

  const dataUrl = await fetchAsDataUrl(src);
  if (!dataUrl) {
    logPdfWarning(PdfReportError.IMAGE_LOAD_FAILED, src);
    return;
  }

  img.setAttribute('src', dataUrl);
  img.removeAttribute('crossorigin');
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'reload', signal: controller.signal });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function withTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    promise.finally(() => {
      window.clearTimeout(timer);
      resolve();
    });
  });
}

export async function renderElementToCanvas(
  element: HTMLElement,
  timeoutMs: number
): Promise<HTMLCanvasElement> {
  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      html2canvas(element, {
        scale: getRenderScale(),
        useCORS: true,
        imageTimeout: 10000,
        backgroundColor: '#ffffff',
      }),
      new Promise<HTMLCanvasElement>((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error('Timeout rendering PDF page')),
          timeoutMs
        );
      }),
    ]);
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
}
